// ── Utility functions for StudyLeague ─────────────────────────────────────

// Format seconds to HH:MM:SS
export function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// Format seconds to human readable (e.g. "2h 30m")
export function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Format date relative (e.g. "2 hours ago")
export function timeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Format date to "Mon, Jan 1"
export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Generate a random 6-char group ID
export function generateGroupId() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return 'GRP-' + Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// Countdown from deadline (returns "2d 3h 14m" or "Expired")
export function countdown(deadlineStr) {
  const deadline = new Date(deadlineStr);
  const now = new Date();
  const diffMs = deadline - now;
  if (diffMs <= 0) return 'Expired';

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// Get streak spine color class
export function getSpineClass(streak) {
  if (streak >= 30) return 'spine-red';
  if (streak >= 7) return 'spine-amber';
  return 'spine-green';
}

// Get streak color
export function getStreakColor(streak) {
  if (streak >= 30) return '#ef4444';
  if (streak >= 7) return '#f59e0b';
  return '#22c55e';
}

// Get progress bar color class
export function getProgressColor(pct) {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

// Copy text to clipboard
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  }
}

// Motivational quotes array
export const MOTIVATIONAL_QUOTES = [
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
  { text: "Success is the sum of small efforts, repeated day in and day out.", author: "Robert Collier" },
  { text: "The harder I work, the luckier I get.", author: "Samuel Goldwyn" },
  { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { text: "Education is the most powerful weapon you can use to change the world.", author: "Nelson Mandela" },
  { text: "Genius is one percent inspiration and ninety-nine percent perspiration.", author: "Thomas Edison" },
  { text: "A mind is like a parachute. It doesn't work if it's not open.", author: "Frank Zappa" },
  { text: "The beautiful thing about learning is that no one can take it away from you.", author: "B.B. King" },
  { text: "Live as if you were to die tomorrow. Learn as if you were to live forever.", author: "Mahatma Gandhi" },
  { text: "Study while others are sleeping; work while others are loafing.", author: "William A. Ward" },
  { text: "Push yourself, because no one else is going to do it for you.", author: "Unknown" },
];

// 8 Pixel-art style avatar colors/styles
export const AVATARS = [
  { bg: '#6366f1', initials: 'A', emoji: '🎓' },
  { bg: '#ec4899', initials: 'B', emoji: '📚' },
  { bg: '#06b6d4', initials: 'C', emoji: '🔬' },
  { bg: '#8b5cf6', initials: 'D', emoji: '🏆' },
  { bg: '#f59e0b', initials: 'E', emoji: '⚡' },
  { bg: '#22c55e', initials: 'F', emoji: '🌟' },
  { bg: '#ef4444', initials: 'G', emoji: '🔥' },
  { bg: '#14b8a6', initials: 'H', emoji: '🎯' },
];

// Chat topic tags
export const CHAT_TAGS = [
  { value: 'Study Help', color: '#6366f1' },
  { value: 'Resource Share', color: '#22c55e' },
  { value: 'Challenge', color: '#f59e0b' },
  { value: 'Off-topic', color: '#94a3b8' },
];

// clsx helper
export function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}
